import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, X, ChevronRight, Bell, Lock } from 'lucide-react';
import apiService from '../../services/apiService';
import { useLanguage } from '../../i18n';

// Deep-merge a partial patch into a prefs object (mutates dst).
function deepAssign(dst, src) {
    Object.keys(src).forEach((k) => {
        if (src[k] && typeof src[k] === 'object' && !Array.isArray(src[k])) {
            dst[k] = dst[k] && typeof dst[k] === 'object' ? dst[k] : {};
            deepAssign(dst[k], src[k]);
        } else {
            dst[k] = src[k];
        }
    });
}

// Build a patch with the SAME shape as `partial` but carrying the values
// currently in `prefs`. Applied on top of an optimistic merge it reverts exactly
// the changed leaves (and nothing else), so a concurrent toggle isn't clobbered.
function buildInversePatch(prefs, partial) {
    const walk = (curr, src) => {
        const out = {};
        Object.keys(src).forEach((k) => {
            const currVal = curr ? curr[k] : undefined;
            if (src[k] && typeof src[k] === 'object' && !Array.isArray(src[k])) {
                out[k] = walk(currVal, src[k]);
            } else {
                out[k] = currVal; // previous value (may be undefined = default)
            }
        });
        return out;
    };
    return walk(prefs, partial);
}

// Notification Center & Delivery Matrix (#191).
// An iPhone-style list of features → a per-feature sub-menu of event toggles +
// a "Delivery Channels" section (Main Office · Telegram). System &
// Security has per-event channel toggles with locked (mandatory) channels.
// ALL copy is English (Language Integrity — card §4).

// A dark-on-dark <select>: `appearance-none` plus an inline chevron, because the
// native UA arrow is drawn in the platform colour and vanishes on this ground.
// The opaque `#12161B` ground is exactly `bg-white/[0.03]` composited over the
// modal ground (#0B0F14) — visually identical to the token, but solid so the
// native option popup stays readable.
export const SELECT_CHEVRON = {
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-opacity='0.5' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 11px center',
};

// The PAI switch: 52×30 track, 24px knob, 3px padding (DESIGN_SYSTEM "Component
// sizes"). Both states carry a 1px border so the box NEVER changes size when it
// flips — notification-jiggle.spec.js measures the containing row to ≤1px
// across a toggle, so only the knob's transform may animate.
function Toggle({ checked, disabled, onChange, testId, label }) {
    return (
        <button
            type="button"
            data-testid={testId}
            data-checked={checked ? 'true' : 'false'}
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => { if (!disabled) onChange(!checked); }}
            className={`relative inline-flex h-[30px] w-[52px] items-center rounded-full border p-[3px] transition-colors duration-150 focus:outline-none focus-visible:border-white/80 flex-shrink-0 ${disabled ? 'opacity-40 cursor-not-allowed ' : ''}${checked ? 'border-transparent bg-white/[0.3]' : 'border-pai bg-white/[0.06]'}`}
        >
            <span className={`inline-block h-6 w-6 transform rounded-full transition-transform duration-150 ${checked ? 'translate-x-[22px] bg-white' : 'translate-x-0 bg-white/45'}`} />
        </button>
    );
}

// A group card: rows separated by hairlines, on the faintest pane wash.
const GROUP_CARD = 'rounded-card border border-pai bg-white/[0.025] overflow-hidden';
// The canonical settings row box (17px / 22px).
const ROW_BOX = 'px-[22px] py-[17px]';

// Telegram is a delivery channel of its own (#191). A phone channel
// is disabled while that account is unlinked; linking it flips the toggle ON
// automatically (the BE default for the channel IS its linked state).
// These are CLIENT-OWNED chrome (unlike the BE-provided taxonomy labels
// elsewhere in this file) — module-level constants can't call the `t` hook, so
// the English fallback lives here and the small `channelLabel`/`channelShort`
// helpers below resolve the translated string wherever a `t` is in scope.
const CHANNEL_LABEL = {
    main_office: 'Main Office',
    telegram: 'Telegram',
};
const CHANNEL_LABEL_KEY = {
    main_office: 'navChannelMainOffice',
    telegram: 'telegram',
};
const channelLabel = (t, ch) => t(CHANNEL_LABEL_KEY[ch] || ch, CHANNEL_LABEL[ch] || ch);

// Compact column captions for the System & Security per-event channel grid.
const CHANNEL_SHORT = {
    main_office: 'Office',
    telegram: 'TG',
};
const CHANNEL_SHORT_KEY = {
    main_office: 'navChannelShortOffice',
    telegram: 'navChannelShortTg',
};
const channelShort = (t, ch) => t(CHANNEL_SHORT_KEY[ch] || ch, CHANNEL_SHORT[ch] || ch);

// #530: WhatsApp is hidden in the web app. Its prefs keys may still arrive from
// the BE; they are never rendered and never PATCHed (a PATCH carries only the
// leaf the user changed), so the server's stored values pass through untouched.
const PHONE_CHANNELS = ['telegram'];
const ALL_CHANNELS = ['main_office', ...PHONE_CHANNELS];

const LINK_HINT = {
    telegram: 'Link Telegram in Settings to enable.',
};
const LINK_HINT_KEY = {
    telegram: 'navLinkTelegramHint',
};
const linkHint = (t, ch) => t(LINK_HINT_KEY[ch] || ch, LINK_HINT[ch] || '');

const NotificationCenter = ({ onExit, onClose }) => {
    const { t } = useLanguage();
    const [data, setData] = useState(null); // {schema, prefs, channels_linked}
    const [feature, setFeature] = useState(null); // selected feature key
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const errorTimerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        apiService.get('/auth/notification-preferences')
            .then((res) => { if (!cancelled) { setData(res); setLoading(false); } })
            .catch(() => { if (!cancelled) { setLoadError(t('navNotifLoadError', 'Could not load notification settings.')); setLoading(false); } });
        return () => { cancelled = true; if (errorTimerRef.current) clearTimeout(errorTimerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Per-channel link state — each phone toggle is greyed out on its own.
    const linked = useMemo(() => ({
        telegram: !!data?.channels_linked?.telegram,
    }), [data]);

    // Toggle handling is OPTIMISTIC + background-saved (Notification Jiggle #223):
    // the switch flips IMMEDIATELY in local state, the PATCH runs in the
    // background with NO layout-shifting "Saving…" indicator, and a failure
    // reverts exactly the changed leaf (+ a non-flow error toast). We deliberately
    // do NOT reconcile the toggles from the PATCH response — the optimistic merge
    // mirrors the server merge exactly for every interactive toggle (locked
    // channels are non-editable), so an out-of-order response can never clobber a
    // newer toggle and cause a visible flip.
    const patch = useCallback(async (partial) => {
        if (errorTimerRef.current) { clearTimeout(errorTimerRef.current); errorTimerRef.current = null; }
        setSaveError(null);

        // Optimistic local merge — flip now, and remember the inverse to revert on failure.
        let inverse = null;
        setData((prev) => {
            if (!prev) return prev;
            const next = JSON.parse(JSON.stringify(prev));
            inverse = buildInversePatch(prev.prefs, partial);
            deepAssign(next.prefs, partial);
            return next;
        });

        try {
            await apiService.patch('/auth/notification-preferences', partial);
        } catch (e) {
            if (inverse) {
                setData((prev) => {
                    if (!prev) return prev;
                    const next = JSON.parse(JSON.stringify(prev));
                    deepAssign(next.prefs, inverse);
                    return next;
                });
            }
            setSaveError(t('navNotifSaveError', 'Could not save. Please try again.'));
            errorTimerRef.current = setTimeout(() => setSaveError(null), 4000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = useMemo(
        () => (data?.schema || []).find((f) => f.key === feature) || null,
        [data, feature],
    );

    const goBack = () => { if (feature) setFeature(null); else onExit(); };

    // `selected.label` is BE-provided taxonomy copy (Language Integrity — card
    // §4) and is left untouched; only the default chrome title is localized.
    const title = selected ? selected.label : t('navNotificationCenterTitle', 'Notification Center');

    return (
        <div data-testid="notification-center" className="relative">
            {/* Header — a 40×40 bordered back button + the screen title, per the
                design's level-2/3 drill-in chrome. */}
            <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3 min-w-0">
                    <button
                        data-testid="notif-back"
                        onClick={goBack}
                        className="pai-icon-btn w-10 h-10 border border-pai flex-shrink-0"
                        title={t('commonBack', 'Back')}
                        aria-label={t('commonBack', 'Back')}
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <h2 className="text-[27px] font-extrabold tracking-[-0.025em] text-white truncate">{title}</h2>
                </div>
                <button
                    onClick={onClose}
                    className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                    title={t('navCloseEsc', 'Close (Esc)')}
                >
                    <X size={18} />
                </button>
            </div>

            {loading && (
                <div data-testid="notif-loading" className="py-8 text-center text-[13px] text-pai-meta">{t('navLoadingEllipsis', 'Loading…')}</div>
            )}
            {/* Initial-load failure only — a standalone screen (no toggles to shift). */}
            {loadError && (
                <div data-testid="notif-error" className="mb-3 text-xs text-pai-red">{loadError}</div>
            )}

            {!loading && data && !selected && (
                <FeatureList schema={data.schema} onOpen={setFeature} />
            )}

            {!loading && data && selected && (
                <FeatureSubmenu
                    feature={selected}
                    prefs={data.prefs[selected.key]}
                    linked={linked}
                    onPatch={patch}
                />
            )}

            {/* Save-failure hint — absolutely positioned so it NEVER shifts the
                list layout (Notification Jiggle #223). Auto-dismisses. */}
            {saveError && (
                <div
                    data-testid="notif-save-error"
                    role="alert"
                    className="absolute left-4 right-4 bottom-2 z-10 rounded-ctl border border-pai-red/50 bg-pai-red/[0.12] text-pai-red text-xs font-semibold px-3 py-2 pointer-events-none text-center backdrop-blur-tile"
                >
                    {saveError}
                </div>
            )}
        </div>
    );
};

function FeatureList({ schema, onOpen }) {
    return (
        <div className={GROUP_CARD}>
            {schema.map((f) => (
                <button
                    key={f.key}
                    data-testid={`notif-feature-${f.key}`}
                    onClick={() => onOpen(f.key)}
                    className={`w-full flex items-center justify-between gap-5 ${ROW_BOX} text-left border-b border-pai-hairline last:border-b-0 hover:bg-white/[0.045] transition-colors`}
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <Bell size={16} className="text-pai-meta flex-shrink-0" />
                        <span className="text-[15px] font-semibold text-white truncate">{f.label}</span>
                    </div>
                    <ChevronRight size={18} className="text-white/45 flex-shrink-0" />
                </button>
            ))}
        </div>
    );
}

function FeatureSubmenu({ feature, prefs, linked, onPatch }) {
    const { t } = useLanguage();
    const isSystem = feature.kind === 'per_event_channels';
    const masterOn = feature.master ? prefs.master !== false : true;

    return (
        <div data-testid={`notif-submenu-${feature.key}`} className="space-y-6">
            {/* Master switch (standard features that have one). `feature.label` is
                BE-provided taxonomy copy and stays untouched; only the surrounding
                client-owned words are translated. */}
            {feature.master && (
                <div className={`flex items-center justify-between gap-5 ${GROUP_CARD} ${ROW_BOX}`}>
                    <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-white">{t('navEnableFeatureNotifications', 'Enable {label} Notifications', { label: feature.label })}</div>
                        <div className="mt-1 text-[13px] text-pai-meta">{t('navSilenceFeatureAlerts', 'Turn off to silence every {label} alert.', { label: feature.label })}</div>
                    </div>
                    <Toggle
                        testId={`notif-master-${feature.key}`}
                        label={t('navEnableFeatureNotificationsAria', 'Enable {label} notifications', { label: feature.label })}
                        checked={masterOn}
                        onChange={(v) => onPatch({ [feature.key]: { master: v } })}
                    />
                </div>
            )}

            {/* Event toggles */}
            <div className={feature.master && !masterOn ? 'opacity-40 pointer-events-none' : ''}>
                <h3 className="pai-eyebrow mb-3">{t('navAlerts', 'Alerts')}</h3>
                <div className={GROUP_CARD}>
                    {feature.events.map((ev) => (
                        <div key={ev.key} className={`${ROW_BOX} border-b border-pai-hairline last:border-b-0`}>
                            <div className="flex items-center justify-between gap-5">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="text-[15px] font-semibold text-white truncate">{ev.label}</div>
                                        {/* #379 — the Notification Center must be the
                                            definitive source of truth. The BE marks an
                                            event `wired: false` when NOTHING dispatches
                                            it yet; rendering it identically to a live
                                            toggle is what let three categories look
                                            switched-on while delivering nothing. Say so
                                            instead of implying it works. */}
                                        {ev.wired === false && (
                                            <span
                                                data-testid={`notif-event-unwired-${feature.key}-${ev.key}`}
                                                className="shrink-0 text-[10px] uppercase tracking-wide text-pai-faint font-semibold border border-pai-hairline rounded px-1.5 py-0.5"
                                            >
                                                {t('navNotifComingSoon', 'Coming soon')}
                                            </span>
                                        )}
                                    </div>
                                    {ev.setting ? (
                                        <EventSetting
                                            featureKey={feature.key}
                                            setting={ev.setting}
                                            value={prefs.settings?.[ev.setting.key]}
                                            onPatch={onPatch}
                                        />
                                    ) : (
                                        ev.desc && <div className="mt-1 text-[13px] text-pai-meta">{ev.desc}</div>
                                    )}
                                </div>
                                {isSystem ? (
                                    <SystemEventChannels
                                        featureKey={feature.key}
                                        event={ev}
                                        value={prefs.events[ev.key]}
                                        linked={linked}
                                        onPatch={onPatch}
                                    />
                                ) : (
                                    <Toggle
                                        testId={`notif-event-${feature.key}-${ev.key}`}
                                        label={ev.label}
                                        checked={prefs.events[ev.key] !== false}
                                        onChange={(v) => onPatch({ [feature.key]: { events: { [ev.key]: v } } })}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Delivery Channels (standard features only — one section per sub-menu) */}
            {!isSystem && (
                <div className={feature.master && !masterOn ? 'opacity-40 pointer-events-none' : ''}>
                    <h3 className="pai-eyebrow mb-3">{t('navDeliveryChannels', 'Delivery Channels')}</h3>
                    <div className={GROUP_CARD}>
                        <ChannelRow
                            testId={`notif-channel-${feature.key}-main_office`}
                            label={channelLabel(t, 'main_office')}
                            checked={prefs.channels.main_office !== false}
                            onChange={(v) => onPatch({ [feature.key]: { channels: { main_office: v } } })}
                        />
                        {PHONE_CHANNELS.map((ch) => (
                            <ChannelRow
                                key={ch}
                                testId={`notif-channel-${feature.key}-${ch}`}
                                label={channelLabel(t, ch)}
                                checked={!!prefs.channels[ch]}
                                disabled={!linked[ch]}
                                hint={!linked[ch] ? linkHint(t, ch) : null}
                                onChange={(v) => onPatch({ [feature.key]: { channels: { [ch]: v } } })}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// An inline scalar setting rendered INSIDE an event's description line (#278):
// "Sent [ 30 minutes ] before an event starts." The copy and the option list both
// come from the BE schema, so the dropdown can never offer a value the engine
// would reject. Saves optimistically through the same `patch` path as a toggle.
function EventSetting({ featureKey, setting, value, onPatch }) {
    const current = value ?? setting.default;
    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[13px] text-pai-meta min-w-0">
            {setting.prefix && <span>{setting.prefix}</span>}
            {/* Shorter than the standalone 40px select — this one sits INSIDE a
                sentence, so it takes the "small control" radius/height instead. */}
            <select
                data-testid={`notif-setting-${featureKey}-${setting.key}`}
                aria-label={setting.prefix || setting.key}
                value={current}
                onChange={(e) => onPatch({
                    [featureKey]: { settings: { [setting.key]: Number(e.target.value) } },
                })}
                style={SELECT_CHEVRON}
                className="h-[34px] max-w-full appearance-none rounded-[9px] border border-pai bg-[#12161b] pl-3 pr-8 text-[13px] font-medium text-white transition-colors hover:border-white/30 focus:outline-none focus:border-white/80"
            >
                {(setting.options || []).map((o) => (
                    <option key={o.value} value={o.value} className="bg-pai-modal text-white">{o.label}</option>
                ))}
            </select>
            {setting.suffix && <span>{setting.suffix}</span>}
        </div>
    );
}

function ChannelRow({ testId, label, checked, disabled, hint, onChange }) {
    return (
        <div className={`${ROW_BOX} border-b border-pai-hairline last:border-b-0 flex items-center justify-between gap-5`}>
            <div className="min-w-0">
                <div className="text-[15px] font-semibold text-white">{label}</div>
                {hint && <div className="mt-1 text-[13px] text-pai-faint">{hint}</div>}
            </div>
            <Toggle testId={testId} label={label} checked={checked} disabled={disabled} onChange={onChange} />
        </div>
    );
}

// System & Security: per-event channel toggles; a locked channel is forced ON
// and rendered as a non-editable lock.
function SystemEventChannels({ featureKey, event, value, linked, onPatch }) {
    const { t } = useLanguage();
    const locks = event.locks || {};
    const set = (channel, v) => onPatch({ [featureKey]: { events: { [event.key]: { [channel]: v } } } });
    return (
        <div className="flex items-center gap-3 flex-shrink-0">
            {ALL_CHANNELS.map((ch) => {
                const locked = !!locks[ch];
                const disabled = locked || (ch !== 'main_office' && !linked[ch]);
                const checked = ch === 'main_office' ? value.main_office !== false : !!value[ch];
                return (
                    <div key={ch} className="flex flex-col items-center gap-1.5">
                        <span className="text-[10px] font-semibold tracking-[0.06em] text-pai-meta whitespace-nowrap">
                            {channelShort(t, ch)}
                        </span>
                        {locked ? (
                            <span
                                data-testid={`notif-syschannel-${event.key}-${ch}-locked`}
                                className="inline-flex h-[30px] w-[52px] items-center justify-center rounded-full border border-pai bg-white/[0.12] text-white/70"
                                title={t('navRequiredAlwaysOn', 'Required — always on')}
                            >
                                <Lock size={13} />
                            </span>
                        ) : (
                            <Toggle
                                testId={`notif-syschannel-${event.key}-${ch}`}
                                // `event.label` is BE-provided taxonomy copy; only the
                                // channel name is a client-owned translation.
                                label={`${event.label} ${channelLabel(t, ch)}`}
                                checked={checked}
                                disabled={disabled}
                                onChange={(v) => set(ch, v)}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default NotificationCenter;
